import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import SEO from '../components/SEO';
import './CustomerAuth.css';

const CustomerLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useCustomerAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Prefer the explicit ?redirect= query param (used by Checkout.js when an
  // existing email tries to guest-checkout), then any location.state.from
  // (set by ProtectedRoute), and finally the customer's account page.
  const params = new URLSearchParams(location.search);
  const redirectParam = params.get('redirect');
  const from = redirectParam || location.state?.from || '/account';

  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="customer-auth-page">
      <SEO title={t('auth.login', 'Login')} />

      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('auth.loginTitle', 'Welcome Back')}</h1>
          <p>{t('auth.loginSubtitle', 'Sign in to view your orders and manage your account')}</p>
        </div>

        {location.state?.message && (
          <div className="auth-success-banner">{location.state.message}</div>
        )}

        {error && <div className="auth-error-banner">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-group">
            <label htmlFor="email">{t('checkout.email', 'Email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              required
              autoFocus
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="password">{t('auth.password', 'Password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
            />
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? t('auth.loggingIn', 'Signing in...') : t('auth.loginBtn', 'Sign In')}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password">{t('auth.forgotPassword', 'Forgot your password?')}</Link>
        </div>

        <div className="auth-footer">
          <p>
            {t('auth.noAccount', "Don't have an account?")}{' '}
            <Link to="/register" className="auth-link-inline">{t('auth.registerBtn', 'Create Account')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
